import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators,} from "@angular/forms";
import {AtheleteUnitsAdapter} from "./AtheleteUnitsAdapter";
import {IGeographicalEntity} from "../models/geography.model";
import {IPaymentInformationForm} from "../../shared/components/update-payment-info-dialog/update-payment-info-dialog.component";

export class CustomValidators {


  public static paymentInformationValidator(
      formGroup: FormGroup<IPaymentInformationForm>
  ): ValidationErrors | null {
    const cardNumberControl = formGroup.controls.cardNumber;
    const cvvControl = formGroup.controls.CVV;

    const acceptedCreditCards = {
      visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
      mastercard:
          /^5[1-5][0-9]{14}$|^2(?:2(?:2[1-9]|[3-9][0-9])|[3-6][0-9][0-9]|7(?:[01][0-9]|20))[0-9]{12}$/,
      amex: /^3[47][0-9]{13}$/,
      discover:
          /^65[4-9][0-9]{13}|64[4-9][0-9]{13}|6011[0-9]{12}|(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})$/,
      diners_club: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
      jcb: /^(?:2131|1800|35[0-9]{3})[0-9]{11}$/
    };

    if (!cardNumberControl || !cvvControl) {
      return null; // În mod normal aruncăm o eroare sau returnăm direct, dacă nu există aceste controale
    }

    const cardNumber = cardNumberControl.value || "";
    const cvv = cvvControl.value || "";

    // 1. Detectăm tipul de card
    let matchedCardType: string | null = null;
    for (const [cardType, regex] of Object.entries(acceptedCreditCards)) {
      if (regex.test(cardNumber)) {
        matchedCardType = cardType;
        break;
      }
    }

    // 2. Validăm numărul cardului
    if (!matchedCardType) {
      cardNumberControl.setErrors({ invalidCardNumber: true });
    } else {
      // Eliminăm eroarea `invalidCardNumber` dacă exista și nu mai e cazul
      if (cardNumberControl.hasError("invalidCardNumber")) {
        const existingErrors = { ...cardNumberControl.errors };
        delete existingErrors["invalidCardNumber"];
        if (Object.keys(existingErrors).length === 0) {
          cardNumberControl.setErrors(null);
        } else {
          cardNumberControl.setErrors(existingErrors);
        }
      }
    }

    // 3. Validăm CVV-ul în funcție de tipul de card
    if (matchedCardType === "amex") {
      // Amex poate avea CVV de 3 sau 4 cifre
      if (!/^\d{3,4}$/.test(cvv)) {
        cvvControl.setErrors({ invalidAmexCVV: true });
      } else {
        if (cvvControl.hasError("invalidAmexCVV")) {
          const existingErrors = { ...cvvControl.errors };
          delete existingErrors["invalidAmexCVV"];
          if (Object.keys(existingErrors).length === 0) {
            cvvControl.setErrors(null);
          } else {
            cvvControl.setErrors(existingErrors);
          }
        }
      }
    } else {
      // Alte carduri necesită CVV de exact 3 cifre
      if (!/^\d{3}$/.test(cvv)) {
        cvvControl.setErrors({ invalidCVV: true });
      } else {
        if (cvvControl.hasError("invalidCVV")) {
          const existingErrors = { ...cvvControl.errors };
          delete existingErrors["invalidCVV"];
          if (Object.keys(existingErrors).length === 0) {
            cvvControl.setErrors(null);
          } else {
            cvvControl.setErrors(existingErrors);
          }
        }
      }
    }

    // Dacă dorim, la final putem returna null pentru a indica faptul că
    // validatorul nu setează nicio eroare la nivel de FormGroup
    return null;
  }

  public static noWhitespaceValidator(control: FormControl): ValidationErrors | null {
    const value = control.value;
    if (value) {
      if (value.toString().match(/\s/)) {
        return { noWhitespace: true };
      }
    }
    return null;
  }

  public static customEmailValidator(control: FormControl): ValidationErrors | null {
    const value = control.value;
    if(value) {
      if(!value.toString().match(/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/i)) return { email: { invalid: true, actualValue: value } };
    }
    return null;
  }

  public static passwordMatchValidator(control: AbstractControl): {
    [key: string]: any;
  } {
    if (
      control?.get("password")?.value !== control?.get("confirmPassword")?.value
    ) {
      control?.get("confirmPassword")?.setErrors({ passwordMatch: true });
      return { passwordMatch: true };
    } else {
      let errors = control?.get("confirmPassword").errors;
      if(control?.get("confirmPassword").hasError('passwordMatch')) {
        delete errors.passwordMatch;
        const keys = Object.keys(errors);
        errors = keys.length > 0 ? errors : null;
        control?.get("confirmPassword")?.setErrors(errors);
        return null;
      }

    }
  }

  public static requiredCategoryValidator(control: AbstractControl): {
    [key: string]: any;
  } {
    if (!control?.value?.id || !control?.value?.name) {
      return { required: true };
    }
  }

  public static noDigitsValidator(
    control: FormControl,
  ): ValidationErrors | null {
    const value = control.value;
    if (value) {
      if (value.toString().match(/[aA-zZ'-]$/)) {
        return null;
      }
      return { noDigits: true };
    }
    return null;
  }

  public static variableCvv(group: FormGroup): void {
    const cardNo =
      (group.controls["cardNumber"].value &&
        group.controls["cardNumber"].value.toString()) ||
      "";
    const cvv =
      (group.controls["CVV"].value && group.controls["CVV"].value.toString()) ||
      "";

      if (cvv.length !== 3 && cvv.length !== 4) {
        group.controls["CVV"].setErrors({
          invalidAmexCVV: true,
          invalidCVV: false,
        });
      }

  }

  public static heightBoundariesValidator(group: FormGroup): void {
    const height = group.controls["height"].value;
    let value;
    if (typeof height === "string" && height.includes("'")) {
      value = AtheleteUnitsAdapter.valueToCm(height);
    } else value = height;
    if (value !== "" && (value < 50 || value > 300)) {
      group.controls["height"].setErrors({ invalidHeight: true });
    }
  }

  public static nameValidator(): ValidatorFn {
    return Validators.pattern("^[A-Za-z \\u00C0-\\u00FF\\u0100-\\u0280'-]*$");
  }

  public static cityValidator(control: FormControl<IGeographicalEntity>): ValidationErrors | null {
    const value: IGeographicalEntity = control.value;
    const cityName = typeof value === "string" ? value : value?.name;
    if (!cityName?.trim()) {
      return {
        required: true,
      };
    }
    if(cityName.length > 100) {
      return {
        maxlength: true
      }
    }
    return null;
  }

  public static floatValidator(): ValidatorFn {
    return Validators.pattern("^\\d+(\\.\\d{1,2})?$");
  }

  public static emailValidator(): ValidatorFn {
    return Validators.pattern(
      '^(([^<>()\\[\\]\\\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@"]+)*)|(".+"))' +
        "@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$",
    );
  }

  public static phoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid = /^[0-9-+\s()]*$/.test(control.value);
      return isValid ? null : { invalidPhone: true };
    };
  }

  public static numberValidator(): ValidatorFn {
    return Validators.pattern("^[0-9]*$");
  }

  public static CVVValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid = /^[0-9]*$/.test(control.value);
      return isValid ? null : { invalidCVV: true };
    };
  }

  public static usernameValidator(): ValidatorFn {
    return Validators.pattern("^[a-zA-Z\\-0-9]*$");
  }

  public static passwordValidator(): ValidatorFn {
    return ((formControl: FormControl) => {
      const value = formControl.value;
      // regex for one uppercase, one lowercase, one number and one special character

      if (
        !value?.match(
          /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/,
        )
      ) {
        return {
          invalidPassword: true,
        };
      }
    }) as ValidatorFn;
  }

  public static codeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid = /^[0-9 ]+$/.test(control.value);
      return isValid ? null : { invalidCVV: true };
    };
  }

  public static zipCodeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid = /^[0-9 ]+$/.test(control.value);
      return isValid ? null : { invalidZipCode: true };
    };
  }

  public static cardValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid =
        /^(?:4[0-9]{12}(?:[0-9]{3})?|[25][1-7][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/.test(
          control.value,
        );
      return isValid ? null : { invalidCardNumber: true };
    };
  }

  public static digitsOnlyValidator(control: FormControl): ValidationErrors | null {
    const value = control.value;
    if (value && /\D/.test(value)) {
      return { invalidOnlyDigits: true };
    }
    return null;
  }

  public static onlyDigitsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid = /^\d+$/.test(control.value);
      return isValid ? null : { invalidOnlyDigits: true };
    };
  }

  public static feetValidator(): ValidatorFn {
    return Validators.pattern(/^\d+'(\d+")?$/gm);
  }
}
